import { getNumericalEntityId } from '@opengov/app-core/entity/selectors';
import { composeEffects } from '@opengov/compose-app/core/module';
import { List, Map } from 'immutable';
import { createStructuredSelector } from '@opengov/compose-app/reselect';
import { getReportGrants, getReportInvitableUsers, updateReportGrants } from '@opengov/api-deprecated';
import { EGranteeType, EReportActionSet, IGrantee } from '@opengov/reporting-types';

import { selectCurrentReportId } from '../../../common/report/selectors';
import { selectCanManageAccess, selectCanViewAccess } from '../../selectors/selectReportExplorerPermissions';
import { DataTableStatus } from '../../types';
import { selectCurrentGrantees } from '../selectors/selectReportAccess';
import { accessTableActionCreators } from '../state/reportAccessTableState';
import { reportActionCreators } from '../../../common/report/state';
import { IToastMuiProps, MuiToastTypes } from '../../../common/helpers/utils';
import { queryClient } from '../../../../src/apiHooks/core/queryClient';
import { foldersAndReportsQueryKeys } from '../../../../src/pages/reportsPage/hooks/useGetFoldersAndReports/foldersAndReports.queryKeys';

export const getOnlyChangedGrantees = (
  existingGrantees: Map<string, IGrantee>,
  updatedGrantees: Map<string, IGrantee>
): List<IGrantee> => {
  const deletedGrantees = existingGrantees.reduce((acc, existingGrantee) => {
    if (!updatedGrantees.has(existingGrantee.granteeId)) {
      return acc.push({ ...existingGrantee, actionSet: 'none' });
    }
    return acc;
  }, List());

  //grantees that have been added or modified
  const modifiedGrantees = updatedGrantees.reduce((acc, updatedGrantee) => {
    if (existingGrantees.get(updatedGrantee.granteeId)) {
      if (existingGrantees.get(updatedGrantee.granteeId).actionSet !== updatedGrantee.actionSet) {
        // add updated grantee
        return acc.push(updatedGrantee);
      } else {
        // skip unmodified grantee
        return acc;
      }
    }
    // add new grantee
    return acc.push(updatedGrantee);
  }, List());
  return deletedGrantees.concat(modifiedGrantees) as any;
};

export const getDefaultGranteeGroups = (entityId: number): Map<string, IGrantee> => {
  return Map([
    [
      `entity_admin`,
      {
        granteeName: 'Entity Admins',
        granteeId: 'entity_admin',
        granteeEmail: '',
        granteeType: EGranteeType.Group,
        actionSet: EReportActionSet.ReportOwner
      }
    ],
    [
      `/entity/${entityId}`,
      {
        granteeName: 'All users',
        granteeId: `/entity/${entityId}`,
        granteeEmail: '',
        granteeType: EGranteeType.Group,
        actionSet: EReportActionSet.ReportNoAccess
      }
    ]
  ]);
};

export const reportAccessEffects = composeEffects('report-access', (compose) => ({
  updateReportGrantees: compose.statefulEffect(
    createStructuredSelector({
      reportId: selectCurrentReportId,
      existingGrantees: selectCurrentGrantees
    }),
    async ({ reportId, existingGrantees }, updatedGrantees: Map<string, IGrantee>) => {
      updateReportGrants({
        reportId,
        grantees: getOnlyChangedGrantees(existingGrantees, updatedGrantees).toArray()
      })
        .then(() => {
          accessTableActionCreators.setCurrentGrantees({ grantees: updatedGrantees });
          const toastProps: IToastMuiProps = {
            type: MuiToastTypes.SUCCESS,
            headerText: 'Report Access Updated',
            bodyText: 'Access to this report has been updated. To make further changes, click "Manage Access."',
            dataTest: 'report-access-update-success'
          };
          reportActionCreators.toggleMuiToast(toastProps);
          void queryClient.invalidateQueries({ queryKey: foldersAndReportsQueryKeys.all });
        })
        .catch(() => {
          const toastProps: IToastMuiProps = {
            type: MuiToastTypes.FAILURE,
            headerText: 'Update Access Failed',
            bodyText: 'We were unable to update the access to this report. Please try again.'
          };
          reportActionCreators.toggleMuiToast(toastProps);
        });
    }
  ),
  getReportGrantees: compose.statefulPayloadlessEffect(
    createStructuredSelector({
      reportId: selectCurrentReportId,
      entityId: getNumericalEntityId,
      canViewAccess: selectCanViewAccess
    }),
    async ({ reportId, entityId, canViewAccess }) =>
      canViewAccess &&
      getReportGrants({
        reportId
      })
        .then((grantees) => {
          const defaultGranteeGroups = getDefaultGranteeGroups(entityId);
          const granteeMap: Map<string, IGrantee> = defaultGranteeGroups.merge(
            Map(grantees.map((grantee) => [grantee.granteeId, grantee]))
          );
          accessTableActionCreators.setAccessTableStatus(DataTableStatus.Current);
          accessTableActionCreators.setCurrentGrantees({
            grantees: granteeMap
          });
          accessTableActionCreators.setTemporaryGrantees({
            grantees: granteeMap
          });
        })
        .catch(() => {
          reportActionCreators.toggleMuiToast({
            type: MuiToastTypes.FAILURE,
            headerText: 'Failed to get existing Access',
            bodyText: 'We were unable to get current access to this report.'
          });
        })
  ),
  getInvitableUsers: compose.statefulPayloadlessEffect(
    createStructuredSelector({
      reportId: selectCurrentReportId,
      canManageAccess: selectCanManageAccess
    }),
    async ({ reportId, canManageAccess }) =>
      canManageAccess &&
      getReportInvitableUsers({
        reportId
      })
        .then((users) => {
          accessTableActionCreators.setInvitableUsers({ grantees: List(users) });
        })
        .catch(() => {
          reportActionCreators.toggleMuiToast({
            type: MuiToastTypes.FAILURE,
            headerText: 'Failed to get invitable users',
            bodyText: 'We were unable to get invitable users for this report.'
          });
        })
  )
}));
